type XlsxCellValue = string | number | boolean | null | undefined;
export type XlsxCellStyle = 'title' | 'header' | 'label' | 'money' | 'default';
export type XlsxCellAlign = 'left' | 'center' | 'right';
export type XlsxCell =
  | XlsxCellValue
  | {
      value: XlsxCellValue;
      style?: XlsxCellStyle;
      align?: XlsxCellAlign;
    };

export interface XlsxSheet {
  name: string;
  rows: XlsxCell[][];
  columns?: number[];
}

const textEncoder = new TextEncoder();
let crcTable: Uint32Array | null = null;

const escapeXml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');

const xmlFile = (content: string): Uint8Array => textEncoder.encode(content);

const getCrcTable = (): Uint32Array => {
  if (crcTable) return crcTable;

  const table = new Uint32Array(256);
  for (let i = 0; i < 256; i += 1) {
    let c = i;
    for (let k = 0; k < 8; k += 1) {
      c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    }
    table[i] = c >>> 0;
  }
  crcTable = table;
  return table;
};

const crc32 = (data: Uint8Array): number => {
  const table = getCrcTable();
  let crc = 0xffffffff;
  for (const byte of data) {
    crc = table[(crc ^ byte) & 0xff] ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
};

const writeUint16 = (target: Uint8Array, offset: number, value: number) => {
  target[offset] = value & 0xff;
  target[offset + 1] = (value >>> 8) & 0xff;
};

const writeUint32 = (target: Uint8Array, offset: number, value: number) => {
  target[offset] = value & 0xff;
  target[offset + 1] = (value >>> 8) & 0xff;
  target[offset + 2] = (value >>> 16) & 0xff;
  target[offset + 3] = (value >>> 24) & 0xff;
};

const concatBytes = (parts: Uint8Array[]): Uint8Array => {
  const size = parts.reduce((sum, part) => sum + part.length, 0);
  const output = new Uint8Array(size);
  let offset = 0;
  for (const part of parts) {
    output.set(part, offset);
    offset += part.length;
  }
  return output;
};

const dosDateTime = () => {
  const now = new Date();
  const time =
    (now.getHours() << 11) |
    (now.getMinutes() << 5) |
    Math.floor(now.getSeconds() / 2);
  const date =
    ((now.getFullYear() - 1980) << 9) |
    ((now.getMonth() + 1) << 5) |
    now.getDate();
  return { date, time };
};

interface ZipFile {
  path: string;
  nameBytes: Uint8Array;
  data: Uint8Array;
  crc: number;
  offset: number;
}

const createZip = (files: Array<{ path: string; data: Uint8Array }>): Uint8Array => {
  const { date, time } = dosDateTime();
  const prepared: ZipFile[] = [];
  const localParts: Uint8Array[] = [];
  let offset = 0;

  for (const file of files) {
    const nameBytes = textEncoder.encode(file.path);
    const crc = crc32(file.data);
    const local = new Uint8Array(30 + nameBytes.length);

    writeUint32(local, 0, 0x04034b50);
    writeUint16(local, 4, 20);
    writeUint16(local, 6, 0);
    writeUint16(local, 8, 0);
    writeUint16(local, 10, time);
    writeUint16(local, 12, date);
    writeUint32(local, 14, crc);
    writeUint32(local, 18, file.data.length);
    writeUint32(local, 22, file.data.length);
    writeUint16(local, 26, nameBytes.length);
    writeUint16(local, 28, 0);
    local.set(nameBytes, 30);

    prepared.push({ path: file.path, nameBytes, data: file.data, crc, offset });
    localParts.push(local, file.data);
    offset += local.length + file.data.length;
  }

  const centralOffset = offset;
  const centralParts: Uint8Array[] = [];

  for (const file of prepared) {
    const central = new Uint8Array(46 + file.nameBytes.length);
    writeUint32(central, 0, 0x02014b50);
    writeUint16(central, 4, 20);
    writeUint16(central, 6, 20);
    writeUint16(central, 8, 0);
    writeUint16(central, 10, 0);
    writeUint16(central, 12, time);
    writeUint16(central, 14, date);
    writeUint32(central, 16, file.crc);
    writeUint32(central, 20, file.data.length);
    writeUint32(central, 24, file.data.length);
    writeUint16(central, 28, file.nameBytes.length);
    writeUint16(central, 30, 0);
    writeUint16(central, 32, 0);
    writeUint16(central, 34, 0);
    writeUint16(central, 36, 0);
    writeUint32(central, 38, 0);
    writeUint32(central, 42, file.offset);
    central.set(file.nameBytes, 46);
    centralParts.push(central);
    offset += central.length;
  }

  const centralSize = offset - centralOffset;
  const end = new Uint8Array(22);
  writeUint32(end, 0, 0x06054b50);
  writeUint16(end, 4, 0);
  writeUint16(end, 6, 0);
  writeUint16(end, 8, prepared.length);
  writeUint16(end, 10, prepared.length);
  writeUint32(end, 12, centralSize);
  writeUint32(end, 16, centralOffset);
  writeUint16(end, 20, 0);

  return concatBytes([...localParts, ...centralParts, end]);
};

const columnName = (index: number): string => {
  let name = '';
  let current = index;
  while (current > 0) {
    const remainder = (current - 1) % 26;
    name = String.fromCharCode(65 + remainder) + name;
    current = Math.floor((current - 1) / 26);
  }
  return name;
};

const sheetName = (name: string): string => {
  const cleaned = name.replace(/[\][:*?/\\]/g, ' ').trim();
  return (cleaned || 'Sheet').slice(0, 31);
};

const cellValue = (cell: XlsxCell): XlsxCellValue =>
  typeof cell === 'object' && cell !== null && 'value' in cell ? cell.value : cell;

const cellStyle = (cell: XlsxCell): number => {
  if (!(typeof cell === 'object' && cell !== null && 'value' in cell)) return 0;
  if (cell.style === 'money' && cell.align === 'center') return 7;
  if (cell.align === 'center') return 5;
  if (cell.align === 'right') return 6;
  if (cell.style === 'title') return 1;
  if (cell.style === 'header') return 2;
  if (cell.style === 'label') return 3;
  if (cell.style === 'money') return 4;
  return 0;
};

const styleAttribute = (cell: XlsxCell): string => {
  const style = cellStyle(cell);
  return style > 0 ? ` s="${style}"` : '';
};

const cellTextLength = (cell: XlsxCell): number => {
  const value = cellValue(cell);
  if (value == null) return 0;
  return String(value).length;
};

const columnWidths = (rows: XlsxCell[][], configured?: number[]): number[] => {
  const totalColumns = Math.max(configured?.length ?? 0, ...rows.map((row) => row.length), 1);
  return Array.from({ length: totalColumns }, (_, index) => {
    if (configured?.[index]) return configured[index];
    const maxContent = rows.reduce(
      (max, row) => Math.max(max, cellTextLength(row[index])),
      0
    );
    return Math.min(Math.max(maxContent + 2, 10), 38);
  });
};

const cellXml = (cell: XlsxCell, rowIndex: number, columnIndex: number): string => {
  const ref = `${columnName(columnIndex)}${rowIndex}`;
  const value = cellValue(cell);
  const style = styleAttribute(cell);
  if (value == null || value === '') return `<c r="${ref}"${style}/>`;
  if (typeof value === 'number' && Number.isFinite(value)) {
    return `<c r="${ref}"${style}><v>${value}</v></c>`;
  }
  if (typeof value === 'boolean') {
    return `<c r="${ref}" t="b"${style}><v>${value ? 1 : 0}</v></c>`;
  }
  return `<c r="${ref}" t="inlineStr"${style}><is><t>${escapeXml(String(value))}</t></is></c>`;
};

const worksheetXml = (sheet: XlsxSheet): string => {
  const widths = columnWidths(sheet.rows, sheet.columns);
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <cols>
    ${widths
      .map(
        (width, index) =>
          `<col min="${index + 1}" max="${index + 1}" width="${width}" customWidth="1"/>`
      )
      .join('')}
  </cols>
  <sheetData>
    ${sheet.rows
      .map(
        (row, rowIndex) =>
          `<row r="${rowIndex + 1}">${row
            .map((cell, columnIndex) => cellXml(cell, rowIndex + 1, columnIndex + 1))
            .join('')}</row>`
      )
      .join('')}
  </sheetData>
</worksheet>`;
};

export function createXlsxBlob(sheets: XlsxSheet[]): Blob {
  const normalizedSheets = sheets.length > 0 ? sheets : [{ name: 'Sheet1', rows: [] }];
  const sheetOverrides = normalizedSheets
    .map(
      (_, index) =>
        `<Override PartName="/xl/worksheets/sheet${index + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`
    )
    .join('');
  const workbookSheets = normalizedSheets
    .map(
      (sheet, index) =>
        `<sheet name="${escapeXml(sheetName(sheet.name))}" sheetId="${index + 1}" r:id="rId${index + 1}"/>`
    )
    .join('');
  const workbookRels = normalizedSheets
    .map(
      (_, index) =>
        `<Relationship Id="rId${index + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${index + 1}.xml"/>`
    )
    .join('');
  const stylesId = normalizedSheets.length + 1;

  const files = [
    {
      path: '[Content_Types].xml',
      data: xmlFile(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
  <Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>
  ${sheetOverrides}
</Types>`),
    },
    {
      path: '_rels/.rels',
      data: xmlFile(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
</Relationships>`),
    },
    {
      path: 'xl/workbook.xml',
      data: xmlFile(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <sheets>${workbookSheets}</sheets>
</workbook>`),
    },
    {
      path: 'xl/_rels/workbook.xml.rels',
      data: xmlFile(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  ${workbookRels}
  <Relationship Id="rId${stylesId}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
</Relationships>`),
    },
    {
      path: 'xl/styles.xml',
      data: xmlFile(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <numFmts count="1">
    <numFmt numFmtId="164" formatCode="$ #,##0"/>
  </numFmts>
  <fonts count="3">
    <font><sz val="11"/><name val="Arial"/></font>
    <font><b/><sz val="11"/><name val="Arial"/><color rgb="FFFFFFFF"/></font>
    <font><b/><sz val="14"/><name val="Arial"/></font>
  </fonts>
  <fills count="4">
    <fill><patternFill patternType="none"/></fill>
    <fill><patternFill patternType="gray125"/></fill>
    <fill><patternFill patternType="solid"><fgColor rgb="FF1F2937"/><bgColor indexed="64"/></patternFill></fill>
    <fill><patternFill patternType="solid"><fgColor rgb="FFEFF6FF"/><bgColor indexed="64"/></patternFill></fill>
  </fills>
  <borders count="2">
    <border><left/><right/><top/><bottom/><diagonal/></border>
    <border>
      <left style="thin"><color rgb="FFD7DEE8"/></left>
      <right style="thin"><color rgb="FFD7DEE8"/></right>
      <top style="thin"><color rgb="FFD7DEE8"/></top>
      <bottom style="thin"><color rgb="FFD7DEE8"/></bottom>
      <diagonal/>
    </border>
  </borders>
  <cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
  <cellXfs count="8">
    <xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>
    <xf numFmtId="0" fontId="2" fillId="0" borderId="0" xfId="0" applyFont="1"/>
    <xf numFmtId="0" fontId="1" fillId="2" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1">
      <alignment horizontal="center"/>
    </xf>
    <xf numFmtId="0" fontId="0" fillId="3" borderId="1" xfId="0" applyFill="1" applyBorder="1"/>
    <xf numFmtId="164" fontId="0" fillId="0" borderId="1" xfId="0" applyNumberFormat="1" applyBorder="1"/>
    <xf numFmtId="0" fontId="0" fillId="0" borderId="1" xfId="0" applyAlignment="1" applyBorder="1">
      <alignment horizontal="center"/>
    </xf>
    <xf numFmtId="0" fontId="0" fillId="0" borderId="1" xfId="0" applyAlignment="1" applyBorder="1">
      <alignment horizontal="right"/>
    </xf>
    <xf numFmtId="164" fontId="0" fillId="0" borderId="1" xfId="0" applyNumberFormat="1" applyAlignment="1" applyBorder="1">
      <alignment horizontal="center"/>
    </xf>
  </cellXfs>
</styleSheet>`),
    },
    ...normalizedSheets.map((sheet, index) => ({
      path: `xl/worksheets/sheet${index + 1}.xml`,
      data: xmlFile(worksheetXml(sheet)),
    })),
  ];

  return new Blob([createZip(files)], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
}
